import { useState, useEffect } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { isNativePlatform } from "@/lib/capacitor-safe";

export function SafariOnlyBanner() {
  const { t } = useLanguage();
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Never show inside the native Capacitor shell (WKWebView is not Safari,
    // but the app runs as an installed native app — the banner would be wrong).
    if (isNativePlatform()) return;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true;

    // Show banner if user is on iOS but NOT using Safari and NOT already installed
    if (isIOS && !isSafari && !isInStandaloneMode) {
      setShowBanner(true);
    }
  }, []);

  if (!showBanner) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 p-4 pt-[max(1rem,env(safe-area-inset-top))] animate-slide-in">
      <Alert className="bg-amber-50 border-amber-200 shadow-lg">
        <AlertCircle className="h-5 w-5 text-amber-600" />
        <AlertDescription className="flex items-start justify-between gap-2">
          <p className="text-sm text-amber-900 flex-1 pr-2">
            {t("safariBanner.message")}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowBanner(false)}
            className="h-6 w-6 p-0 hover:bg-amber-100"
          >
            <X className="h-4 w-4" />
          </Button>
        </AlertDescription>
      </Alert>
    </div>
  );
}